import { type Player, PlayerStatus, Role, type Room, type Vote } from '../schemas';
import type { Rng } from './rng';

/** Fisher-Yates shuffle via injected RNG. Non-mutating. */
export const shuffle = <T>(items: readonly T[], rng: Rng): T[] => {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
};

/**
 * Role split. Clochard takes 1 seat if enabled and `playerCount ≥ 5`.
 * Infiltré clamped to `[1, playerCount - clochard - 1]`. Below 3 players: all flics.
 */
export const computeRoleDistribution = (
  playerCount: number,
  includeClochard: boolean,
  requestedInfiltre?: number,
): { infiltre: number; clochard: number; flics: number } => {
  if (playerCount < 3) return { infiltre: 0, clochard: 0, flics: playerCount };
  const clochard = includeClochard && playerCount >= 5 ? 1 : 0;
  const maxInfiltre = Math.max(1, playerCount - clochard - 1);
  const requested = requestedInfiltre ?? Math.max(1, Math.floor(playerCount / 4));
  const infiltre = Math.min(maxInfiltre, Math.max(1, requested));
  return { infiltre, clochard, flics: playerCount - infiltre - clochard };
};

/** Returns roles aligned to `players[i]`. */
export const assignRoles = (
  players: readonly Player[],
  includeClochard: boolean,
  rng: Rng,
  requestedInfiltre?: number,
): Role[] => {
  const dist = computeRoleDistribution(players.length, includeClochard, requestedInfiltre);
  const pool: Role[] = [
    ...Array.from({ length: dist.infiltre }, () => Role.Infiltre),
    ...Array.from({ length: dist.clochard }, () => Role.Clochard),
    ...Array.from({ length: dist.flics }, () => Role.Flic),
  ];
  return shuffle(pool, rng);
};

export const livingPlayers = (room: Room): Player[] =>
  room.players.filter((p) => p.status === PlayerStatus.Alive);

/** Every living player except the voter. */
export const validVoteTargets = (room: Room, voterId: string): Player[] =>
  livingPlayers(room).filter((p) => p.id !== voterId);

/** `tied` true when ≥2 targets share top count; `topId` null when no votes. */
export const tallyVotes = (votes: readonly Vote[]): { topId: string | null; tied: boolean } => {
  const counts = new Map<string, number>();
  for (const v of votes) counts.set(v.target, (counts.get(v.target) ?? 0) + 1);
  let topId: string | null = null;
  let topCount = 0;
  let tied = false;
  for (const [id, c] of counts) {
    if (c > topCount) {
      topId = id;
      topCount = c;
      tied = false;
    } else if (c === topCount) {
      tied = true;
    }
  }
  return { topId, tied };
};

/**
 * Flics win when no Infiltré/Clochard alive. Infiltré wins on parity.
 * Clochard wins as last alive. `null` while ongoing.
 */
export const computeWinner = (room: Room): Role | null => {
  const alive = livingPlayers(room);
  const aliveInfiltre = alive.filter((p) => p.role === Role.Infiltre).length;
  const aliveClochard = alive.filter((p) => p.role === Role.Clochard).length;
  const aliveFlic = alive.filter((p) => p.role === Role.Flic).length;

  if (aliveInfiltre === 0 && aliveClochard === 0) return Role.Flic;
  if (aliveInfiltre >= aliveFlic + aliveClochard) return Role.Infiltre;
  if (aliveFlic === 0 && aliveClochard > 0) return Role.Clochard;
  return null;
};
